/**
 * Finding a batch in the brew log — the list's search box, status chips, and
 * sort control all share this so matching is identical wherever it runs.
 *
 * Matching follows the recipe list: case- and accent-insensitive prefix
 * matching, AND across query tokens. The haystack is the batch name, the
 * frozen recipe's name/tags/style, and the status — the things a brewer
 * actually types when they are looking for "the pale that's fermenting".
 */

import { getStyle, normalize, styleCode } from '@beerwright/catalog';
import { BATCH_STATUSES, type BatchDoc, type BatchStatus } from '@beerwright/commands';
import { naturalCompare } from '@/lib/natural-sort';

/** Split a string into the tokens {@link batchMatchesQuery} compares. */
function tokensOf(value: string): readonly string[] {
  const normalized = normalize(value);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/** Name, recipe, style, tags, and status — everything a batch can be found by. */
function searchParts(batch: BatchDoc): readonly string[] {
  const recipe = batch.recipeSnapshot;
  const style = recipe.styleId === null ? undefined : getStyle(recipe.styleId);
  const parts: string[] = [batch.name, recipe.name, batch.status, ...recipe.tags];
  if (style !== undefined) parts.push(style.name, styleCode(style));
  return parts;
}

/**
 * True when every query token prefixes some token in the batch's name, recipe,
 * style, tags, or status. An empty (or whitespace-only) query matches
 * everything — status chips still narrow on their own.
 */
export function batchMatchesQuery(batch: BatchDoc, query: string): boolean {
  const queryTokens = tokensOf(query);
  if (queryTokens.length === 0) return true;
  const haystack = searchParts(batch).flatMap(tokensOf);
  return queryTokens.every((token) => haystack.some((part) => part.startsWith(token)));
}

/**
 * True when the batch is in the selected set of statuses. An empty set matches
 * everything. OR, not AND: a batch has exactly one status, so requiring two
 * would match nothing.
 */
export function batchHasStatus(batch: BatchDoc, statuses: readonly BatchStatus[]): boolean {
  if (statuses.length === 0) return true;
  return statuses.includes(batch.status);
}

/** Batches matching a free-text query and an optional set of statuses. */
export function filterBatches(
  batches: readonly BatchDoc[],
  options: { readonly query?: string; readonly statuses?: readonly BatchStatus[] } = {},
): readonly BatchDoc[] {
  const query = options.query ?? '';
  const statuses = options.statuses ?? [];
  return batches.filter(
    (batch) => batchMatchesQuery(batch, query) && batchHasStatus(batch, statuses),
  );
}

/** Statuses that appear in a batch list, in pipeline order. */
export function collectBatchStatuses(batches: readonly BatchDoc[]): readonly BatchStatus[] {
  const seen = new Set<BatchStatus>();
  for (const batch of batches) seen.add(batch.status);
  return BATCH_STATUSES.filter((status) => seen.has(status));
}

/** How the brew log can be ordered. Default is newest brew — the axis a log has. */
export const BATCH_SORTS = ['brewed-desc', 'brewed-asc', 'name', 'status', 'updated'] as const;

/** @see BATCH_SORTS */
export type BatchSort = (typeof BATCH_SORTS)[number];

/** Labels for the sort control. */
export const BATCH_SORT_LABELS: Readonly<Record<BatchSort, string>> = {
  'brewed-desc': 'Newest brew',
  'brewed-asc': 'Oldest brew',
  name: 'Name',
  status: 'Status',
  updated: 'Last updated',
};

const STATUS_ORDER: Readonly<Record<BatchStatus, number>> = Object.fromEntries(
  BATCH_STATUSES.map((status, index) => [status, index]),
) as Record<BatchStatus, number>;

/** Brew instant used for chronology: unbrewed sorts as "about to happen". */
function brewKey(batch: BatchDoc): number {
  return batch.brewedAt ?? Number.POSITIVE_INFINITY;
}

const COMPARE: Readonly<Record<BatchSort, (a: BatchDoc, b: BatchDoc) => number>> = {
  'brewed-desc': (a, b) => brewKey(b) - brewKey(a) || b.createdAt - a.createdAt,
  'brewed-asc': (a, b) => brewKey(a) - brewKey(b) || a.createdAt - b.createdAt,
  name: (a, b) => naturalCompare(a.name, b.name) || b.createdAt - a.createdAt,
  status: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || brewKey(b) - brewKey(a),
  updated: (a, b) => b.updatedAt - a.updatedAt || b.createdAt - a.createdAt,
};

/**
 * Order a batch list.
 *
 * `brewed-desc` is the brew-log default: unbrewed first (a batch you are about
 * to brew), then most recently brewed, ties on `createdAt`. The other keys
 * exist because a log with a dozen fermenters in it is also a "what's
 * fermenting" list and an A–Z of names.
 */
export function sortBatches(batches: readonly BatchDoc[], sort: BatchSort): readonly BatchDoc[] {
  return [...batches].sort(COMPARE[sort]);
}
